// ABOUTME: Form prototype 5 — Form replaces the bottom composer entirely.
// ABOUTME: Chat stays bright above; the form occupies the input-bar slot.

import { supabase } from "@/contexts/AuthContext";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { jwtDecode } from "jwt-decode";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  Pencil,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";

interface JWTPayload {
  user_role?: string;
}

export const Route = createFileRoute("/_authenticated/5")({
  beforeLoad: async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw redirect({ to: "/login" });
    }
    const decoded = jwtDecode<JWTPayload>(session.access_token);
    if (decoded.user_role !== "superadmin") {
      throw redirect({ to: "/" });
    }
  },
  component: Prototype5,
});

const STEPS = [
  { key: "info", label: "Info" },
  { key: "setup", label: "Setup" },
  { key: "pin1", label: "Pin 1" },
  { key: "pin2", label: "Pin 2" },
  { key: "pin3", label: "Pin 3" },
  { key: "signoff", label: "Sign-off" },
] as const;
type StepKey = (typeof STEPS)[number]["key"];

function Prototype5() {
  const [stepIdx, setStepIdx] = useState(1);
  const [formOpen, setFormOpen] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const showMessages = !(formOpen && fullscreen && !submitted);

  return (
    <div className="relative flex h-full flex-1 flex-col">
      {/* messages */}
      <div
        className={`relative overflow-y-auto px-4 md:px-12 pt-4 pb-0 ${
          showMessages ? "flex-1" : "hidden"
        }`}
      >
        <Msg from="user">
          Log a wicket pin hardness test for batch BWM471-30-20-002.
        </Msg>
        <Msg from="assistant">
          <p>
            Starting the <strong>Wicket Pin Hardness Test</strong> — six short
            steps. I prefilled date, tester, batch, supplier, and quantity from
            your last test on this line.
          </p>
          <div className="not-prose mt-3 flex items-center gap-2">
            <span className="inline-flex h-6 items-center gap-1.5 rounded-full border border-border bg-background px-2.5 text-[11px] text-muted-foreground">
              <span className="inline-block h-1 w-1 rounded-full bg-foreground" />
              {submitted
                ? "hardness test · submitted"
                : formOpen
                  ? `filling hardness test · step ${stepIdx + 1} of 6`
                  : "hardness test · paused"}
            </span>
            {!formOpen && !submitted && (
              <button
                type="button"
                onClick={() => setFormOpen(true)}
                className="text-xs underline-offset-2 hover:underline"
              >
                resume
              </button>
            )}
          </div>
        </Msg>
        <Msg from="user">sounds good</Msg>
        <Msg from="assistant">
          <p>
            The form is in your input area below — I'll stay up here so you can
            ask me anything mid-fill.
          </p>
        </Msg>
        {submitted && (
          <Msg from="assistant">
            <p>
              Submitted — average across the three pins came in at{" "}
              <strong>60.3 HRC</strong>, well inside spec. I've tagged the
              record to <strong>Line 2 Poucher</strong> and{" "}
              <strong>Ferguson Industrial</strong> so it's searchable later.
            </p>
          </Msg>
        )}
        <div className="h-4" />
      </div>

      {/* bottom slot: form OR composer */}
      {formOpen && !submitted ? (
        <FormPanel
          stepIdx={stepIdx}
          fullscreen={fullscreen}
          onBack={() => setStepIdx((p) => Math.max(0, p - 1))}
          onNext={() => {
            if (stepIdx === STEPS.length - 1) {
              setSubmitted(true);
              setFormOpen(false);
              setFullscreen(false);
            } else {
              setStepIdx((p) => p + 1);
            }
          }}
          onClose={() => {
            setFormOpen(false);
            setFullscreen(false);
          }}
          onToggleFullscreen={() => setFullscreen((f) => !f)}
        />
      ) : (
        <Composer
          hasResumable={!submitted && !formOpen}
          onResume={() => setFormOpen(true)}
        />
      )}
    </div>
  );
}

/* ─── shell pieces ────────────────────────────────────────────────────── */

function Msg({
  from,
  children,
}: {
  from: "user" | "assistant";
  children: React.ReactNode;
}) {
  const isUser = from === "user";
  return (
    <div className="mb-4">
      <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
        <div className="flex max-w-[95%] flex-col items-start md:max-w-[80%]">
          <div
            className={`w-full min-w-0 overflow-hidden rounded-lg p-4 ${
              isUser ? "bg-primary/75 text-white" : "bg-muted"
            }`}
          >
            <div
              className={`prose prose-sm max-w-none ${isUser ? "prose-invert" : ""}`}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Composer({
  hasResumable,
  onResume,
}: {
  hasResumable: boolean;
  onResume: () => void;
}) {
  return (
    <div className="border-t border-border bg-muted/30 p-4">
      <div className="mx-auto max-w-7xl space-y-3">
        {hasResumable && (
          <div className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="flex h-5 w-5 items-center justify-center rounded border border-border bg-muted/40 font-mono text-[9px] font-semibold tracking-wider">
                WPH
              </span>
              <span>Hardness test draft paused on Setup</span>
            </div>
            <Button size="sm" variant="outline" onClick={onResume}>
              Resume
            </Button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <SelectorChip label="Line" value="Line 2 Poucher" />
          <SelectorChip label="Machine type" value="All types" />
          <SelectorChip label="Language" value="English" />
        </div>
        <div className="flex gap-2">
          <div className="flex min-h-[44px] flex-1 items-start rounded-md border border-input bg-background px-3 py-2.5 text-sm text-muted-foreground">
            Ask anything…
          </div>
          <Button size="icon" className="h-11 w-11">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function SelectorChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex h-9 items-center rounded-md border border-input bg-background px-3 text-sm">
        {value}
        <ChevronDown className="ml-2 h-3.5 w-3.5 text-muted-foreground" />
      </div>
    </div>
  );
}

/* ─── form panel (occupies the composer slot) ─────────────────────────── */

function FormPanel({
  stepIdx,
  fullscreen,
  onBack,
  onNext,
  onClose,
  onToggleFullscreen,
}: {
  stepIdx: number;
  fullscreen: boolean;
  onBack: () => void;
  onNext: () => void;
  onClose: () => void;
  onToggleFullscreen: () => void;
}) {
  const currentKey = STEPS[stepIdx].key as StepKey;
  const isLast = stepIdx === STEPS.length - 1;

  return (
    <div
      className={`flex border-t border-border bg-background ${
        fullscreen ? "min-h-0 flex-1 flex-col" : "flex-col"
      }`}
    >
      {/* slim header — replaces the selectors row visually */}
      <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-2.5 md:px-12">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background">
            <span className="font-mono text-[10px] font-semibold tracking-wider">
              WPH
            </span>
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">
              Wicket Pin Hardness Test
            </div>
            <div className="truncate text-xs text-muted-foreground">
              BWM471-30-20-002 · Line 2 Poucher · draft
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
          >
            Save & close
          </button>
          <button
            type="button"
            onClick={onToggleFullscreen}
            title={fullscreen ? "Exit fullscreen" : "Expand to fullscreen"}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
          >
            {fullscreen ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Pause and return to chat input"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* body */}
      <div className={`px-4 md:px-12 ${fullscreen ? "min-h-0 flex-1" : ""}`}>
        <div
          className={`mx-auto grid max-w-7xl grid-cols-[1fr_220px] gap-6 py-4 ${
            fullscreen ? "h-full" : "h-[42vh]"
          }`}
        >
          {/* left column: scrollable content */}
          <div className="min-h-0 min-w-0 overflow-y-auto pr-2">
            <StepBody stepKey={currentKey} />
          </div>

          {/* right rail: stepper (table of contents) — itself scrolls; nav stays pinned beneath */}
          <div className="flex min-h-0 flex-col gap-3 border-l border-border pl-6">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <Stepper stepIdx={stepIdx} />
            </div>

            <div className="flex flex-col gap-2 border-t border-border pt-3">
              <Button size="sm" onClick={onNext} className="gap-1.5">
                {isLast ? "Submit" : "Next"}
                {!isLast && <ChevronRight className="h-3.5 w-3.5" />}
              </Button>
              <button
                type="button"
                onClick={onBack}
                disabled={stepIdx === 0}
                className="flex items-center justify-center gap-1.5 rounded-md border border-border bg-background py-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </button>
              <span className="pt-1 text-[11px] text-muted-foreground">
                Auto-saving · saved 12s ago
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stepper({ stepIdx }: { stepIdx: number }) {
  return (
    <ol className="space-y-2">
      {STEPS.map((s, i) => {
        const state =
          i < stepIdx ? "done" : i === stepIdx ? "current" : "pending";
        return (
          <li key={s.key} className="flex items-center gap-2.5 text-sm">
            <span
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                state === "done"
                  ? "border-foreground bg-foreground text-background"
                  : state === "current"
                    ? "border-foreground bg-background ring-2 ring-foreground/15"
                    : "border-border bg-background"
              }`}
            >
              {state === "done" && <Check className="h-3 w-3" />}
              {state === "current" && (
                <span className="h-1.5 w-1.5 rounded-full bg-foreground" />
              )}
            </span>
            <span
              className={
                state === "pending"
                  ? "text-muted-foreground"
                  : state === "current"
                    ? "font-medium"
                    : ""
              }
            >
              {s.label}
            </span>
            {state === "current" && (
              <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {i + 1} / {STEPS.length}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* ─── step bodies ─────────────────────────────────────────────────────── */

function StepHeading({
  eyebrow,
  title,
  hint,
}: {
  eyebrow: string;
  title: string;
  hint?: string;
}) {
  return (
    <div className="mb-4">
      <div className="mb-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
        {eyebrow}
      </div>
      <h2 className="text-lg font-medium tracking-tight">{title}</h2>
      {hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}
    </div>
  );
}

function StepBody({ stepKey }: { stepKey: StepKey }) {
  if (stepKey === "info") return <InfoStep />;
  if (stepKey === "setup") return <SetupStep />;
  if (stepKey === "pin1" || stepKey === "pin2" || stepKey === "pin3")
    return <PinStep pin={stepKey} />;
  return <SignoffStep />;
}

function SheetField({
  label,
  value,
  placeholder,
  prefilled,
  type = "text",
}: {
  label: string;
  value?: string;
  placeholder?: string;
  prefilled?: boolean;
  type?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        {label}
        {prefilled && (
          <span className="flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            <Sparkles className="h-2.5 w-2.5" />
            agent
          </span>
        )}
      </span>
      <input
        type={type}
        defaultValue={value}
        placeholder={placeholder}
        className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
    </div>
  );
}

function InfoStep() {
  return (
    <>
      <StepHeading
        eyebrow="Step 1 of 6"
        title="Test information"
        hint="A few details about the batch and the run."
      />
      <div className="grid grid-cols-3 gap-x-4 gap-y-3">
        <SheetField label="Date" value="2026-05-25" type="date" prefilled />
        <SheetField label="Tested by" value="R. Arkin" prefilled />
        <SheetField label="Batch / PO" value="BWM471-30-20-002" prefilled />
        <SheetField label="Supplier" value="Ferguson Industrial" prefilled />
        <SheetField label="Quantity in batch" type="number" placeholder="500" />
        <SheetField
          label="Number of pins tested"
          type="number"
          placeholder="3"
        />
      </div>
    </>
  );
}

function SetupStep() {
  const items = [
    "Diamond (Rockwell C) indenter installed",
    "V-Anvil installed for round pins",
    "Weight selector set to 150 kg (1471 N)",
    "Calibration verified using HRC test block",
    "Test block reading within ±1 HRC of stated value",
  ];
  const [checked, setChecked] = useState<boolean[]>(items.map(() => false));
  const done = checked.filter(Boolean).length;
  return (
    <>
      <StepHeading
        eyebrow="Step 2 of 6"
        title="Setup checklist"
        hint="Tap each row to confirm before testing."
      />
      <div className="overflow-hidden rounded-md border border-border">
        {items.map((item, i) => (
          <button
            key={item}
            type="button"
            onClick={() =>
              setChecked((prev) => prev.map((c, idx) => (idx === i ? !c : c)))
            }
            className={`flex w-full items-center justify-between gap-3 bg-background px-4 py-2.5 text-left transition ${
              i > 0 ? "border-t border-border" : ""
            } hover:bg-muted/40`}
          >
            <div className="flex items-center gap-3 text-sm">
              <span
                className={`flex h-5 w-5 items-center justify-center rounded border ${
                  checked[i]
                    ? "border-foreground bg-foreground text-background"
                    : "border-border"
                }`}
              >
                {checked[i] && <Check className="h-3 w-3" />}
              </span>
              <span
                className={
                  checked[i] ? "text-muted-foreground line-through" : ""
                }
              >
                {item}
              </span>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {checked[i] ? "RA" : "init"}
            </span>
          </button>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{done} / 5 confirmed</span>
        <span
          className={`font-mono uppercase tracking-wider ${
            done === 5 ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          {done === 5 ? "ready" : "pending"}
        </span>
      </div>
    </>
  );
}

function PinStep({ pin }: { pin: "pin1" | "pin2" | "pin3" }) {
  const n = pin[3];
  const [vals, setVals] = useState(["", "", ""]);
  const nums = vals.map(Number).filter((v) => !Number.isNaN(v) && v);
  const avg = nums.length === 3 ? nums.reduce((a, b) => a + b, 0) / 3 : null;
  const corrected = avg !== null ? avg + 1.5 : null;
  const result =
    corrected === null
      ? null
      : corrected >= 58 && corrected <= 62
        ? "pass"
        : corrected < 58
          ? "fail"
          : "review";

  return (
    <>
      <StepHeading
        eyebrow={`Step ${2 + Number(n)} of 6 · readings`}
        title={`Pin ${n}`}
        hint="Three Rockwell C readings on the smooth cylindrical surface, ≥ 0.125 in (3 mm) from any edge."
      />
      <div className="overflow-hidden rounded-md border border-border">
        <div className="grid grid-cols-3 divide-x divide-border">
          {["T1", "T2", "T3"].map((label, i) => (
            <div key={label} className="bg-background p-4">
              <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {label}
              </div>
              <input
                type="number"
                value={vals[i]}
                onChange={(e) =>
                  setVals((prev) =>
                    prev.map((v, idx) => (idx === i ? e.target.value : v)),
                  )
                }
                placeholder="—"
                className="w-full bg-transparent text-right font-mono text-2xl tabular-nums focus:outline-none placeholder:text-muted-foreground/50"
              />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 divide-x divide-border border-t border-border bg-muted/30">
          {[
            ["Average", avg?.toFixed(1)],
            ["Corrected (+1.5)", corrected?.toFixed(1)],
            ["Result", null],
          ].map(([label, val], i) => (
            <div key={label as string} className="p-3">
              <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {label}
              </div>
              {i === 2 ? (
                <ResultChip result={result} />
              ) : (
                <div className="text-right font-mono text-sm tabular-nums">
                  {(val as string | undefined) ?? "—"}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function ResultChip({
  result,
}: {
  result: "pass" | "fail" | "review" | null;
}) {
  if (!result)
    return <span className="font-mono text-sm text-muted-foreground">—</span>;
  const styles = {
    pass: "bg-foreground text-background",
    fail: "border border-foreground bg-background text-foreground",
    review:
      "border border-dashed border-foreground bg-background text-foreground",
  } as const;
  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 font-mono text-xs uppercase tracking-wider ${styles[result]}`}
    >
      {result}
    </span>
  );
}

function SignoffStep() {
  return (
    <>
      <StepHeading
        eyebrow="Step 6 of 6"
        title="Sign-off"
        hint="Your signature confirms the test. Supervisor sign-off can happen later from the record."
      />
      <div className="space-y-2.5">
        {["Maintenance Technician", "Maintenance Supervisor"].map((role, i) => (
          <div
            key={role}
            className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-md border border-border bg-background p-3"
          >
            <div>
              <div className="mb-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {role}
              </div>
              <div className="text-sm">
                {i === 0 ? "R. Arkin" : "—"}
                {i === 0 && (
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    2026-05-25
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs ${
                i === 0
                  ? "border-foreground bg-foreground text-background"
                  : "border-dashed border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              {i === 0 ? (
                <Check className="h-3 w-3" />
              ) : (
                <Pencil className="h-3 w-3" />
              )}
              {i === 0 ? "Signed" : "Sign"}
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
